import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthUser } from "../../types/auth";

/* =========================
   STATE TYPES
========================= */

export interface AuthState {
  token?: string;
  user?: AuthUser;
  hydrated: boolean;
}

/* =========================
   STORAGE CONSTANTS
========================= */

const AUTH_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const STORAGE_KEY = "auth_state";
const STORAGE_EXPIRES_KEY = "auth_expires";

/* =========================
   INITIAL STATE
========================= */

const initialState: AuthState = {
  token: undefined,
  user: undefined,
  hydrated: false,
};

/* =========================
   SLICE
========================= */

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Store token + user and persist with expiry
     */
    setCredentials(
      state,
      action: PayloadAction<{
        token: string;
        user: AuthUser;
      }>
    ) {
      const { token, user } = action.payload;

      state.token = token;
      state.user = user;
      state.hydrated = true;

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }));
        localStorage.setItem(
          STORAGE_EXPIRES_KEY,
          String(Date.now() + AUTH_TTL_MS)
        );
      } catch (err) {
        console.warn("auth: failed to persist credentials", err);
      }
    },

    /**
     * Update user object only (profile updates)
     */
    updateUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;

      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            token: state.token,
            user: action.payload,
          })
        );
      } catch (err) {
        console.warn("auth: failed to persist user update", err);
      }
    },

    /**
     * Clear auth state and storage (logout)
     */
    clearCredentials(state) {
      state.token = undefined;
      state.user = undefined;
      state.hydrated = true;

      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_EXPIRES_KEY);
      } catch {
        // ignore
      }
    },

    /**
     * Load auth state from localStorage if valid
     * MUST be called once on app startup
     */
    loadFromStorage(state) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const expires = Number(
          localStorage.getItem(STORAGE_EXPIRES_KEY) || "0"
        );

        // Expired or missing
        if (!raw || !expires || Date.now() > expires) {
          state.token = undefined;
          state.user = undefined;
          state.hydrated = true;

          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_EXPIRES_KEY);
          return;
        }

        const parsed = JSON.parse(raw) as {
          token?: string;
          user?: AuthUser;
        };

        state.token = parsed.token;
        state.user = parsed.user;
        state.hydrated = true;
      } catch {
        state.token = undefined;
        state.user = undefined;
        state.hydrated = true;
      }
    },
  },
});

/* =========================
   EXPORTS
========================= */

export const { setCredentials, updateUser, clearCredentials, loadFromStorage } =
  authSlice.actions;

export default authSlice.reducer;
